import * as React from 'react';

import {
  Accordion,
  Button,
  Checkbox,
  MultipleAccordion,
  Popover,
  PopoverDialog,
  PopoverTrigger,
  Row,
  SearchField,
} from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import { LucideListFilter } from 'lucide-react';

import { $filterAccounts } from '../../../_state/scaleTicketPro.state';
import { useTranslations } from '../../../i18n/useTranslations';

type AccountFilterPopoverProps = {
  direction: 'inbound' | 'outbound';
  selectedIds: string[];
  onToggle: (id: string, isSelected: boolean) => void;
  onClearAll: () => void;
};

const isSupplier = (a: { relationship: { isSupplier: boolean } }) =>
  a.relationship.isSupplier;
const isCustomer = (a: { relationship: { isCustomer: boolean } }) =>
  a.relationship.isCustomer;

//
// ⚛️ ⚛️ ⚛️ ACCOUNT FILTER POPOVER ⚛️ ⚛️ ⚛️
//
export const AccountFilterPopover = ({
  direction,
  selectedIds,
  onToggle,
  onClearAll,
}: AccountFilterPopoverProps) => {
  const t = useTranslations();

  // States
  const [search, setSearch] = React.useState('');

  // Atoms
  const { data: accountsData } = useAtomValue($filterAccounts);

  const accountFilter = direction === 'inbound' ? isSupplier : isCustomer;
  const sectionLabel =
    direction === 'inbound'
      ? t.scaleTicketPro.suppliers
      : t.scaleTicketPro.customers;
  const searchPlaceholder =
    direction === 'inbound'
      ? t.scaleTicketPro.searchSuppliers
      : t.scaleTicketPro.searchCustomers;
  const emptyLabel =
    direction === 'inbound'
      ? t.scaleTicketPro.noSuppliersFound
      : t.scaleTicketPro.noCustomersFound;

  // Memoized values
  const accounts = React.useMemo(() => {
    const all = accountsData?.accounts.filter(accountFilter) ?? [];
    if (!search) return all;
    const term = search.toLowerCase();
    return all.filter((a) => a.name.toLowerCase().includes(term));
  }, [accountsData?.accounts, accountFilter, search]);

  const activeFilterCount = selectedIds.length;
  const sectionId = sectionLabel.toLowerCase().replace(/\s+/g, '-');

  return (
    <PopoverTrigger>
      <Button
        variant={activeFilterCount > 0 ? 'solid' : 'outline'}
        size="icon"
        className="h-13 w-12 min-w-12 border-2"
        icon={<LucideListFilter size={20} />}
      />
      <Popover placement="bottom start" className="w-80">
        <PopoverDialog className="flex max-h-96 flex-col">
          <MultipleAccordion variant="ghost" defaultExpandedKeys={[sectionId]}>
            <Accordion
              id={sectionId}
              trigger={
                <Row className="flex-1 items-center justify-between">
                  <span>{sectionLabel}</span>
                  {activeFilterCount > 0 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onClearAll();
                      }}
                      className="text-xs text-primary hover:underline"
                    >
                      {t.scaleTicketPro.clearAll}
                    </button>
                  )}
                </Row>
              }
              variant="ghost"
            >
              <div className="space-y-3 pb-3">
                <SearchField
                  placeholder={searchPlaceholder}
                  value={search}
                  onChange={setSearch}
                  className="mt-1 mb-3"
                />

                {accounts.map((account) => (
                  <Checkbox
                    key={account.id}
                    isSelected={selectedIds.includes(account.id)}
                    onChange={(isSelected) => onToggle(account.id, isSelected)}
                    className="flex items-center gap-2 text-base"
                  >
                    {account.name}
                  </Checkbox>
                ))}

                {accounts.length === 0 && (
                  <div className="py-2 text-center text-xs text-foreground/50">
                    {emptyLabel}
                  </div>
                )}
              </div>
            </Accordion>
          </MultipleAccordion>
        </PopoverDialog>
      </Popover>
    </PopoverTrigger>
  );
};
